import type {
  DroppedApplicantData,
  PendingApplicantData,
  RejectedApplicantData,
} from '@platform/client/schemas';
import { PillBadge } from '@platform/components';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { NameCell } from '../NameCell';
import { capitalize } from './utils';

const pendingCh = createColumnHelper<PendingApplicantData>();
export const pendingColumns: ColumnDef<PendingApplicantData>[] = [
  pendingCh.accessor('name', {
    id: 'name',
    header: 'Participant Name',
    cell: ({ row }) => <NameCell name={row.original.name} email={row.original.email} />,
  }),
  pendingCh.accessor('applicationDate', {
    id: 'applicationSubmitted',
    header: 'Application Submitted',
    cell: ({ row }) =>
      row.original.applicationDate ? formatDateStr(row.original.applicationDate) : '—',
  }),
  pendingCh.accessor(
    (row) => {
      if (!row.applicationDate) return 'Incomplete';
      if (row.applicationStatus === 'waitlisted') return 'Waitlisted';
      return 'Under Review';
    },
    {
      id: 'applicationStatus',
      header: 'Application Status',
      cell: ({ row }) => {
        if (!row.original.applicationDate) {
          return (
            <PillBadge variant='plain' color='orange' size='sm'>
              Incomplete
            </PillBadge>
          );
        }
        if (row.original.applicationStatus === 'waitlisted') {
          return (
            <PillBadge variant='plain' color='gray' size='sm'>
              Waitlisted
            </PillBadge>
          );
        }
        return (
          <PillBadge variant='plain' color='blue' size='sm'>
            Under Review
          </PillBadge>
        );
      },
    },
  ),
] as ColumnDef<PendingApplicantData>[];

const rejectedCh = createColumnHelper<RejectedApplicantData>();
export const rejectedColumns: ColumnDef<RejectedApplicantData>[] = [
  rejectedCh.accessor('name', {
    id: 'name',
    header: 'Participant Name',
    cell: ({ row }) => <NameCell name={row.original.name} email={row.original.email} />,
  }),
  rejectedCh.accessor('rejectedDate', {
    id: 'rejectedDate',
    header: 'Rejected Date',
    cell: ({ row }) =>
      row.original.rejectedDate ? formatDateStr(row.original.rejectedDate) : null,
  }),
] as ColumnDef<RejectedApplicantData>[];

const droppedCh = createColumnHelper<DroppedApplicantData>();
export const droppedColumns: ColumnDef<DroppedApplicantData>[] = [
  droppedCh.accessor('name', {
    id: 'name',
    header: 'Participant Name',
    cell: ({ row }) => <NameCell name={row.original.name} email={row.original.email} />,
  }),
  droppedCh.accessor('droppedDate', {
    id: 'droppedDate',
    header: 'Dropped Date',
    cell: ({ row }) =>
      row.original.droppedDate ? formatDateStr(row.original.droppedDate) : null,
  }),
  droppedCh.accessor('matchStatus', {
    id: 'lastAction',
    header: 'Last Action',
    cell: ({ row }) => (row.original.matchStatus ? capitalize(row.original.matchStatus) : null),
  }),
] as ColumnDef<DroppedApplicantData>[];
